/**
 * Форматирование ФИО «Фамилия И. О.» (p3-structure-sout-training D4, §4/§5). Одна чистая
 * функция, переиспользуемая волной 4 (сотрудники в списке рабочих мест «Спецоценки») и волной 5
 * (ФИО под программой обучения). Принимает либо готовую строку `fullName` (как её отдаёт API —
 * `EmployeeSummary.fullName`/`TrainingProgramEmployee.fullName` и т.п.), либо разложенные части —
 * на случай, если у вызывающего уже есть отдельные поля фамилии/имени/отчества.
 *
 * Разбор строки — по пробелам, ПЕРВЫЕ ТРИ слова трактуются как фамилия/имя/отчество (порядок,
 * принятый во всём проекте — см. `validateFio`); лишние слова после третьего игнорируются.
 * Отчество необязательно — тогда одна инициала («Иванов И.»). Одно слово (без имени вовсе) —
 * фамилия без точки (регистр первой буквы всё равно нормализуется, см. `capitalizeFirst`).
 * Пустой ввод → пустая строка.
 */
export interface FullNameParts {
  lastName: string
  firstName?: string
  middleName?: string
}

function initial(part: string | undefined): string {
  const trimmed = (part ?? '').trim()
  return trimmed ? `${trimmed.charAt(0).toUpperCase()}.` : ''
}

/**
 * Капитализация ПЕРВОЙ буквы фамилии (Opus-ревью волны 4, LOW). Ни клиентская `validateFio`
 * (`lib/validation.ts`), ни серверная `domain.ValidFIO` (`backend/internal/domain/requisites.go`)
 * не проверяют регистр — только «минимум 3 слова» и «без точек» — значит записанное в БД ФИО
 * может реально прийти строчными буквами (опечатка при вводе). Правим ТОЛЬКО первый символ всей
 * строки фамилии — не трогаем остальные буквы, чтобы не сломать двойные/дефисные фамилии
 * («Петрова-Водкина» не превращаем в «Петрова-Водкина» с заглавной после дефиса — этого не
 * умеем и не пытаемся, только первая буква). Инициалы имени/отчества уже капитализируются
 * отдельно в `initial()` независимо от регистра исходных данных.
 */
function capitalizeFirst(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s
}

function formatParts({ lastName, firstName, middleName }: FullNameParts): string {
  const last = capitalizeFirst(lastName.trim())
  if (!last) return ''
  const initials = [initial(firstName), initial(middleName)].filter(Boolean).join(' ')
  return initials ? `${last} ${initials}` : last
}

/** Формат «Фамилия И. О.» из полной строки ФИО или уже разложенных частей. */
export function formatShortName(input: string | FullNameParts): string {
  if (typeof input !== 'string') return formatParts(input)
  const words = input.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return ''
  const [lastName, firstName, middleName] = words
  return formatParts({ lastName, firstName, middleName })
}
